import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { betaAPI } from '../services/api';
import { ArrowLeft, Flask, Plus, Trash, Copy } from '@phosphor-icons/react';

export default function AdminBetaPage() {
  const [betaUsers, setBetaUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newMaxUses, setNewMaxUses] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([betaAPI.adminListUsers(), betaAPI.adminListCodes()]);
      setBetaUsers(u.data.users || []);
      setCodes(c.data.codes || []);
    } catch {
      toast.error('Failed to load beta data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCode = async () => {
    try {
      await betaAPI.adminCreateCode({
        label: newLabel || undefined,
        max_uses: newMaxUses ? parseInt(newMaxUses, 10) : undefined,
        custom_code: customCode || undefined,
      });
      setNewLabel(''); setNewMaxUses(''); setCustomCode('');
      toast.success('Code created');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create code');
    }
  };

  const deleteCode = async (code) => {
    if (!confirm(`Delete code ${code}?`)) return;
    try {
      await betaAPI.adminDeleteCode(code);
      toast.success('Code deleted');
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggleUser = async (user_id, is_beta) => {
    try {
      await betaAPI.adminToggleUser(user_id, is_beta);
      toast.success(is_beta ? 'User added to beta' : 'User removed from beta');
      await load();
    } catch {
      toast.error('Update failed');
    }
  };

  const promoteByEmail = async () => {
    // Reuse admin-users API to find then toggle
    if (!searchEmail.trim()) return;
    try {
      const { adminAPI } = await import('../services/api');
      const { data } = await adminAPI.getUsers({ q: searchEmail.trim() });
      const list = data.users || data || [];
      const target = list.find(u => u.email === searchEmail.trim());
      if (!target) return toast.error('No user with that email');
      await toggleUser(target.user_id, true);
      setSearchEmail('');
    } catch {
      toast.error('Lookup failed');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white" data-testid="admin-beta-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/dashboard" className="text-[#999999] hover:text-[#22C55E] flex items-center gap-2 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Flask className="w-6 h-6 text-[#3B82F6]" />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Beta Access</h1>
        </div>
        <p className="text-[#999] text-sm mb-8">Manage friends & family who get early access to in-progress features.</p>

        {loading ? <p className="text-[#999]">Loading…</p> : (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Codes */}
            <section className="border border-[#222] rounded-xl p-5">
              <h2 className="font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Invite Codes</h2>
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input placeholder="Label (e.g. Family)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="bg-[#111] border-[#333] text-sm" data-testid="code-label-input" />
                <Input placeholder="Max uses (blank = ∞)" value={newMaxUses} onChange={(e) => setNewMaxUses(e.target.value)} className="bg-[#111] border-[#333] text-sm" data-testid="code-maxuses-input" />
                <Input placeholder="Custom code (optional)" value={customCode} onChange={(e) => setCustomCode(e.target.value.toUpperCase())} className="bg-[#111] border-[#333] text-sm" data-testid="code-custom-input" />
              </div>
              <Button onClick={createCode} className="bg-[#22C55E] hover:bg-[#16A34A] text-white w-full mb-4 rounded-sm" data-testid="create-code-btn">
                <Plus className="w-4 h-4 mr-1" /> Create Invite Code
              </Button>

              {codes.length === 0 ? <p className="text-xs text-[#666]">No codes yet.</p> : (
                <div className="space-y-2">
                  {codes.map(c => (
                    <div key={c.code} className="bg-[#0d0d0d] border border-[#222] rounded-lg px-4 py-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <code className="text-[#22C55E] font-mono text-sm font-bold">{c.code}</code>
                          <span className="text-[10px] text-[#666]">{c.label}</span>
                        </div>
                        <p className="text-[10px] text-[#666]">
                          {c.uses_count}/{c.max_uses ?? '∞'} uses
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copy(c.code)} data-testid={`copy-${c.code}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[#EF4444]" onClick={() => deleteCode(c.code)} data-testid={`delete-${c.code}`}>
                          <Trash className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Beta users */}
            <section className="border border-[#222] rounded-xl p-5">
              <h2 className="font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Beta Users</h2>
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 mb-4 flex gap-2">
                <Input placeholder="Promote by email…" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} className="bg-[#111] border-[#333] text-sm" data-testid="promote-email-input" />
                <Button onClick={promoteByEmail} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-sm" data-testid="promote-btn">Promote</Button>
              </div>

              {betaUsers.length === 0 ? <p className="text-xs text-[#666]">No beta users yet.</p> : (
                <div className="space-y-2">
                  {betaUsers.map(u => (
                    <div key={u.user_id} className="bg-[#0d0d0d] border border-[#222] rounded-lg px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">{u.name || u.username}</p>
                        <p className="text-[10px] text-[#666]">{u.email}</p>
                      </div>
                      <Switch checked={true} onCheckedChange={() => toggleUser(u.user_id, false)} data-testid={`toggle-${u.user_id}`} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
